(function () {
  const TAG_NAMES_WITH_VALUE = [
    'INPUT',
    'SELECT',
    'TEXTAREA',
  ];

  const NON_TEXT_INPUT_TYPES = [
    'radio',
    'checkbox',
  ];

  const MAX_SELECT_OPTIONS_TO_DISPLAY = 13;

  const isTextMagnifierEnabled = () => {
    // I don't love digging through AccessiBe's private local storage state to test whether
    // magnifier is enabled, but I'm not seeing any other API from AccessiBe to test the
    // enabled/disabled state of a particular content adjustment.
    const acsbStateJson = localStorage.getItem('acsbState');
    if (!acsbStateJson) return false;
    try {
      const acsbState = JSON.parse(acsbStateJson);
      return acsbState.actions.magnifier?.enabled ?? false;
    } catch (e) {
      console.error(
        "Value for localStorage key 'acsbState' unexpectedly could not be parsed as JSON. Assuming AccessiBe Text Magnifier is turned off."
      );
      return false;
    }
  };

  const DATA_ACSB_TOOLTIP = 'data-acsb-tooltip';
  // This ends up being an important tuning parameter. Want it fairly short, b/c we only need our
  // data-acsb-tooltip value in place until AccessiBe's had time to construct its tooltip DOM. But
  // if we make this too short (e.g. 0), then we end up resetting the attribute before AccessiBe's
  // event handler has even had a chance to fire.
  const TEMPORARY_TOOLTIP_ATTR_DURATION_MILLIS = 15;
  const tooltipStateByElement = new WeakMap();

  /**
   * Temporarily set the given element's data-acsb-tooltip attribute. The attribute will be reset
   * back to its current value after enough time has passed for AccessiBe to construct its tooltip
   * DOM.
   * @param {*} element The element for which to set the attribute.
   * @param {*} tooltipValue The attribute value to set.
   */
  const temporarilySetAcsbTooltip = (element, tooltipValue) => {
    let state = tooltipStateByElement.get(element);
    if (state === undefined) {
      state = { previousTooltip: element.getAttribute(DATA_ACSB_TOOLTIP), timeoutId: undefined };
      tooltipStateByElement.set(element, state);
    }
    element.setAttribute(DATA_ACSB_TOOLTIP, tooltipValue);
    clearTimeout(state.timeoutId);
    const newTimeoutId = setTimeout(
      () => {
        const latestState = tooltipStateByElement.get(element);
        if (latestState !== undefined) {
          const { previousTooltip } = latestState;
          if (previousTooltip === null) {
            element.removeAttribute(DATA_ACSB_TOOLTIP);
          } else {
            element.setAttribute(DATA_ACSB_TOOLTIP, previousTooltip);
          }
          tooltipStateByElement.delete(element);
        }
      },
      TEMPORARY_TOOLTIP_ATTR_DURATION_MILLIS,
    );
    tooltipStateByElement.set(element, { ...state, timeoutId: newTimeoutId });
  }

  // As of this writing, mouseover and focusin are the two event types that AccessiBe listens to to
  // fire its text magnifier tooltips. So we listen to the same events, but in the capture phase,
  // that way we can be sure our handler gets a chance to run anytime AccessiBe is about to display
  // a tooltip.
  ['mouseover', 'focusin'].forEach(eventType => {
    document.addEventListener(
      eventType,
      ({ target }) => {
        if (TAG_NAMES_WITH_VALUE.includes(target.tagName)) {
          if (target.tagName === 'INPUT' && NON_TEXT_INPUT_TYPES.includes(target.type)) return;
          if (!isTextMagnifierEnabled()) return;

          // AccessiBe's text magnifier content adjustment determines what text should go inside its
          // tooltips by inspecting several different attributes of the target element with a final
          // fallback to the target element's inner text. AccessiBe uses the first non-empty
          // attribute/text it finds. As of this writing, the full list of attributes in priority
          // order is
          //
          //   - data-acsb-tooltip
          //   - title
          //   - data-acsb-tmp-title
          //   - aria-label
          //   - alt
          //   - placeholder
          //   - value
          //   - Fallback to inner text
          //
          // Unfortunately, AccessiBe does NOT consult elements' value PROPERTY, just their value
          // attriubte, which doesn't ordinarily update as the user updates interactive elements.
          // So, to get magnifier to magnify user input, we set data-acsb-tooltip ourselves.
          let tooltipValue;
          if (target.tagName === 'SELECT') {
            // For <select> elements, build a bigger tooltip that includes the selected option plus
            // several nearby options. This isn't perfect. It doesn't track the supplier's cursor
            // position within the browser-provided list of options, and it doesn't track "draft"
            // selections that the supplier might make by typing with their keyboard. Getting
            // AccessiBe to magnify individual options probably requires building/incorporating a
            // custom dropdown.
            const displayableOptions = Array.from(target.options).filter(
              // Don't show hidden options in our tooltip unless they're currently selected.
              option => option.selected || !option.hidden,
            );
            const selectedIndex = displayableOptions.findIndex(option => option.selected);
            const startIndex = Math.max(
              0,
              Math.min(
                selectedIndex - Math.floor(MAX_SELECT_OPTIONS_TO_DISPLAY / 2),
                displayableOptions.length - MAX_SELECT_OPTIONS_TO_DISPLAY,
              ),
            );
            tooltipValue = displayableOptions
                .slice(startIndex, startIndex + MAX_SELECT_OPTIONS_TO_DISPLAY)
                .map(
                  // It's scary that AccessiBe treats the tooltip value as HTML, b/c that's an XSS
                  // vulnerability. (Imagine hovering over a <code> element displaying an example
                  // <script> tag!) But that's its current behavior, and it is handy for being able
                  // to style our larger tooltip here. For escaping interpolated HTML here and
                  // below, we're using the `new Option(...).innerHTML` trick from
                  // https://stackoverflow.com/a/22706073.
                  (option, index) => {
                    if (index === 0 && startIndex > 0) {
                      return `<div>( ${startIndex + 1} more options \u2B61)</div>`;
                    }
                    if (
                      index === MAX_SELECT_OPTIONS_TO_DISPLAY - 1 &&
                      startIndex + MAX_SELECT_OPTIONS_TO_DISPLAY < displayableOptions.length
                    ) {
                      return `<div>( ${
                        displayableOptions.length - startIndex - MAX_SELECT_OPTIONS_TO_DISPLAY + 1
                      } more options \u2B63)</div>`;
                    }
                    return `<div${
                      option.selected ? ' style="font-size: 1.1em; font-weight: bold"' : ''
                    }>${
                      new Option(
                        option.selected ? option.label.toLocaleUpperCase() : option.label,
                      ).innerHTML
                    }</div>`;
                  })
                .join('');
          } else {
            tooltipValue = new Option(target.value ?? '').innerHTML;
          }
          temporarilySetAcsbTooltip(target, tooltipValue);
        }
      },
      // Make sure to run in the capture phase, BEFORE AccessiBe has fired its own handlers for
      // these same event types.
      true,
    );
  });
})();
